'use client';
import NextImage, { ImageProps } from 'next/image';
import ErrorBoundary from '../ErrorBoundary';
import imageLoader from '../../utils/imageLoader';
import screens from '../../utils/screens';

type Screens = 'sm' | 'md' | 'lg' | 'xl';
type Sizes = Partial<Record<Screens, number>>;

export interface BetterImageProps extends Omit<ImageProps, 'sizes'> {
  intrinsicSize?: boolean;
  sizes?: Sizes | string;
}

interface Props {
  src: string;
  sizes: Sizes;
  quality?: number;
  width: number;
}

const offsetScreens = {
  _: screens.sm,
  sm: screens.md,
  md: screens.lg,
  lg: screens.xl,
  xl: '50000px',
};

const getSrcSet = ({ src, sizes, quality, width }: Props) =>
  Object.entries({
    _: width,
    ...sizes,
  })
    .map(([screen, size]) => {
      const _src = imageLoader({
        src,
        width: size,
        quality,
      });
      return `${_src} ${offsetScreens[screen as keyof typeof offsetScreens].replace('px', 'w')}`;
    })
    .join(', ');

const defaultSizes: Sizes = {
  sm: 1024,
  md: 1440,
  lg: 1680,
  xl: 2000,
};

export const Image = (props: BetterImageProps) => {
  const src = props.src as string;
  const isAmplienceImage =
    src?.startsWith?.('https://i8.amplience.net') ||
    src?.startsWith?.('https://traegergrills.a.bigcontent.io') ||
    src?.startsWith?.('https://cdn.media.amplience.net');

  if (isAmplienceImage && props.intrinsicSize) {
    const quality = props.quality as number;
    const width = (props.width ?? 768) as number;
    const sizes = (props.sizes ?? defaultSizes) as Sizes;
    const srcSet = props.sizes && getSrcSet({ src, sizes, quality, width });

    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        loading="lazy"
        src={imageLoader({
          src,
          width,
          quality,
        })}
        srcSet={srcSet}
        alt={props.alt}
        className={props.className}
      />
    );
  }

  return (
    <ErrorBoundary>
      <NextImage
        {...props}
        sizes={props.sizes as string}
        loader={isAmplienceImage ? imageLoader : undefined}
      />
    </ErrorBoundary>
  );
};

export default Image;
